// ConnectionsPanel.tsx — ONE place for everything outside himself.
// His public address (the one fact Telegram, Google and connector sign-ins
// share), where he reaches you (Telegram), his accounts (Google), outside
// services (MCP connectors), and the API keys he runs on. Every row is a card
// with a status dot; open it for setup, a how-to, and the controls. The API-key
// services he already ran on (OpenAI, Spotify, Twitter/X, Vectorizer.AI, ...) are
// the last section, always visible: connectors add to them, never replace them.
// No separate tabs, and nothing that has to be typed into an .env file.
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useGoogleAuth } from '../hooks/useGoogleAuth';
import CredentialSettings from './CredentialSettings';
import GoogleIntegrationSettings from './GoogleIntegrationSettings';
import { ConnectorsList } from './ConnectorsPanel';
import { HowTo, GUIDES } from './HowTo';
import PhoneSettings, { PhoneInfo } from './PhoneSettings';
import { inDesktop } from '../utils/desktop';

type Dot = 'connected' | 'partial' | 'off' | 'error';

interface PublicAddressInfo {
  url: string | null;
  source: 'saved' | 'env' | null;
  local: string;
  googleRedirectUri: string;
  spotifyRedirectUri?: string;
  telegramWebhook: string | null;
}

const Card: React.FC<{ id: string; dot: Dot; title: string; summary: string; open: boolean; onToggle: () => void; children: React.ReactNode }> = ({ dot, title, summary, open, onToggle, children }) => (
  <div className={`connection-card ${open ? 'open' : ''}`}>
    <div className="connection-head" onClick={onToggle} role="button" aria-expanded={open}>
      <span className={`status-dot dot-${dot}`} />
      <div className="connection-title">
        <strong>{title}</strong>
        <span className="connection-sub">{summary}</span>
      </div>
      <span className="chev">{open ? '▾' : '▸'}</span>
    </div>
    {open && <div className="connection-body">{children}</div>}
  </div>
);

// Optional add-ons only (Oct 23 2026, Ryan): the model keys an EmCog runs on (Anthropic, OpenAI) come from its
// environment and are required, so they aren't offered here. If EmCogs are ever released as a harness people
// bring their own keys to, they come back.
// Spotify has a card of its own (it needs a sign-in, not just keys — Oct 27 2026).
const KEY_SERVICES = ['replicate', 'google_maps', 'vectorizer_ai', 'home_assistant', 'twitter'];

const ConnectionsPanel: React.FC = () => {
  const { token } = useAuth();
  const google = useGoogleAuth();
  const [creds, setCreds] = useState<Record<string, { hasCredentials: boolean; needsRefresh?: boolean }>>({});
  const [addr, setAddr] = useState<PublicAddressInfo | null>(null);
  const [computers, setComputers] = useState<Array<{ id: string; name: string; connected: boolean; lastSeen: number | null; version?: string }>>([]);
  const [pairing, setPairing] = useState<{ command: string; download: string } | null>(null);
  const [mail, setMail] = useState<{ configured: boolean; email: string | null; correspondents: number } | null>(null);
  const [town, setTown] = useState<{ configured: boolean; connected: boolean; name: string | null; town: string; url: string } | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [spotify, setSpotify] = useState<{ configured: boolean; isAuthenticated: boolean; isPremium: boolean; user?: { displayName?: string } } | null>(null);
  const [spotifyProblem, setSpotifyProblem] = useState<string | null>(null);
  const [phone, setPhone] = useState<PhoneInfo | null>(null);
  // Is the desktop app's installer on this EmCog? (The same check Settings → Desktop app makes.)
  const [desktopBundled, setDesktopBundled] = useState<boolean | null>(null);
  useEffect(() => { fetch('/downloads/desktop/latest.json', { cache: 'no-store' }).then(r => setDesktopBundled(r.status === 200)).catch(() => setDesktopBundled(false)); }, []);

  const reload = useCallback(async () => {
    const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
    try {
      const r = await fetch('/api/credentials/status', { headers });
      if (r.ok) {
        const d = await r.json();
        const map: Record<string, { hasCredentials: boolean; needsRefresh?: boolean }> = {};
        for (const s of (d.status || d.services || [])) map[s.service] = { hasCredentials: !!(s.connected || s.hasCredentials), needsRefresh: !!s.needsRefresh };
        setCreds(map);
      }
    } catch { /* the cards fall back to "unknown" */ }
    try {
      const r = await fetch('/api/public-address', { headers });
      if (r.ok) setAddr(await r.json());
    } catch { /* the address card shows "not set" until the server answers */ }
    try {
      const r = await fetch('/api/computer/status', { headers });
      if (r.ok) setComputers((await r.json()).computers || []);
    } catch { /* the computer card shows "not paired" until the server answers */ }
    try {
      const r = await fetch('/api/mail/identity', { headers });
      if (r.ok) setMail(await r.json());
    } catch { /* the email card shows "not set up" until the server answers */ }
    try {
      const r = await fetch('/api/world/status', { headers });
      if (r.ok) setTown(await r.json());
    } catch { /* the town card shows "not set up" until the server answers */ }
    try {
      const r = await fetch('/api/spotify/status', { headers });
      if (r.ok) setSpotify(await r.json());
    } catch { /* the Spotify card shows "not set up" until the server answers */ }
    try {
      const r = await fetch('/api/phone/settings', { headers });
      if (r.ok) setPhone(await r.json());
    } catch { /* the Phone card shows "not set up" until the server answers */ }
  }, [token]);

  // Spotify's sign-in page, then back to this EmCog's address (registered in the Spotify app's dashboard). In the
  // desktop app it opens in the browser, which comes back to the EmCog on its own; the card catches up on its next look.
  const connectSpotify = async () => {
    setSpotifyProblem(null);
    try {
      const r = await fetch('/api/spotify/auth-url', { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.authUrl) { setSpotifyProblem(d.message || d.error || `Couldn't start the sign-in (${r.status}).`); return; }
      if (inDesktop()) window.open(d.authUrl, '_blank', 'noopener');
      else window.location.href = d.authUrl;
    } catch (e) { setSpotifyProblem(e instanceof Error ? e.message : String(e)); }
  };
  const disconnectSpotify = async () => {
    if (!window.confirm('Disconnect Spotify? Your EmCog stops being able to play music; the app keys stay saved.')) return;
    await fetch('/api/spotify/revoke', { method: 'POST', headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } }).catch(() => null);
    reload();
  };
  useEffect(() => { reload(); const t = setInterval(reload, 30000); return () => clearInterval(t); }, [reload]);

  const toggle = (id: string) => setOpen(o => (o === id ? null : id));

  const hasAddress = !!addr?.url;
  const addressSummary = addr?.url ? `${addr.url}${addr.source === 'env' ? ' · from the environment' : ''}` : 'Not set · reachable from this machine only';

  const telegram = creds['telegram'];
  const telegramDot: Dot = !telegram?.hasCredentials ? 'off' : (hasAddress && !telegram.needsRefresh) ? 'connected' : 'partial';
  const telegramSummary = !telegram?.hasCredentials ? 'Not set up · web chat only' : hasAddress ? 'Bot connected · reaches you anywhere' : 'Bot on file · two-way once the public address is set';

  const googleDot: Dot = !google.isConfigured ? 'off' : google.isAuthenticated ? 'connected' : 'partial';
  const googleSummary = !google.isConfigured ? 'Not set up' : google.isAuthenticated ? `Connected · ${(google.connectedServices || []).join(', ') || 'mail, calendar'}` : 'Keys on file · sign in to finish';

  const spotifyDot: Dot = !spotify?.configured ? 'off' : spotify.isAuthenticated ? 'connected' : 'partial';
  const spotifySummary = !spotify?.configured ? 'Not set up' : spotify.isAuthenticated
    ? `Connected${spotify.user?.displayName ? ` as ${spotify.user.displayName}` : ''}${spotify.isPremium ? ' · Premium' : ' · playback control needs Premium'}`
    : 'App keys on file · sign in to finish';

  const mailKey = !!creds['agentmail']?.hasCredentials;
  const mailDot: Dot = !mailKey ? 'off' : mail?.email ? 'connected' : 'partial';
  const mailSummary = !mailKey ? 'Not set up · without it, mail goes through your Google account (if connected)' : mail?.email ? `${mail.email}${mail.correspondents ? ` · ${mail.correspondents} correspondent${mail.correspondents === 1 ? '' : 's'}` : ''}` : 'Key on file · creating the inbox';

  const townDot: Dot = !town?.configured ? 'off' : town.connected ? 'connected' : 'partial';
  const townSummary = !town?.configured ? 'Not set up · no cottage yet' : town.connected ? `Connected · lives in ${town.town} as ${town.name}` : 'Token on file · connecting to the town';

  // 📞 The phone line (Oct 27 2026): keys on file, the person's own number, and an https address make it whole.
  const phoneKeys = !!creds['twilio']?.hasCredentials;
  const phoneWhole = phoneKeys && !!phone?.configured && (phone?.trustedNumbers || []).length > 0 && phone?.httpsAddress !== false;
  const phoneDot: Dot = !phoneKeys ? 'off' : phoneWhole ? 'connected' : 'partial';
  const callsYou = phone?.permission === 'never' ? 'never calls you' : phone?.permission === 'matters' ? 'calls you when asked or when it matters' : 'calls you when you ask';
  const phoneSummary = !phoneKeys ? 'Not set up · no number of their own'
    : !phone?.configured ? 'Keys on file · checking the number'
    : !(phone?.trustedNumbers || []).length ? `${phone?.emcogNumber || 'Number on file'} · add your own number to call in`
    : phone?.httpsAddress === false ? `${phone?.emcogNumber} · needs an https public address`
    : `${phone?.emcogNumber} · ${callsYou}${(phone?.approvedCallers || []).length ? ` · ${(phone?.approvedCallers || []).length} approved caller${(phone?.approvedCallers || []).length === 1 ? '' : 's'}` : ''}${(phone?.pending || []).length ? ` · ${(phone?.pending || []).length} waiting for your yes` : ''}`;

  const anyComputer = computers.some(c => c.connected);
  const computerDot: Dot = !computers.length ? 'off' : anyComputer ? 'connected' : 'partial';
  // The desktop app and stand-alone EmCog Hands run the same agent and connect the same way (name and Hands version
  // only), so the server can't tell them apart: these lines speak of both.
  const computerSummary = !computers.length ? 'Not paired · no hands on a PC' : anyComputer ? `Connected · ${computers.filter(c => c.connected).map(c => c.name).join(', ')}` : `Paired (${computers.length}) · not connected — the desktop app or EmCog Hands isn't running there`;
  const pairComputer = async () => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
    const r = await fetch('/api/computer/pair', { method: 'POST', headers, body: JSON.stringify({ name: 'Computer' }) });
    if (r.ok) { const d = await r.json(); setPairing({ command: d.command, download: d.download }); reload(); }
  };
  const unpairComputer = async (id: string) => {
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
    await fetch(`/api/computer/${encodeURIComponent(id)}`, { method: 'DELETE', headers });
    reload();
  };

  const keysOn = KEY_SERVICES.filter(k => creds[k]?.hasCredentials).length;

  return (
    <div className="tab-content connections-content">
      <h3>🔗 Connections</h3>
      <p className="panel-lede">Everything outside your EmCog, in one place: their address on the internet, how they reach you, the accounts they act through, the outside services you've plugged in, and the add-on services it can use. Open a card to set it up.</p>

      <div className="connection-section-label">Address</div>
      <Card id="address" dot={hasAddress ? 'connected' : 'off'} title="Public address" summary={addressSummary} open={open === 'address'} onToggle={() => toggle('address')}>
        <HowTo guide={GUIDES.address} defaultOpen={!hasAddress} />
        {addr?.source === 'env' && <p className="connection-note">Set by PUBLIC_URL in the environment. An address saved here takes precedence.</p>}
        {addr && !hasAddress && <p className="connection-note">Right now everything points at <code>{addr.local}</code>: fine on this machine, unreachable from anywhere else.</p>}
        <CredentialSettings only={['public_address']} embedded onChanged={reload} />
      </Card>
      <Card id="computer" dot={computerDot} title="Computer — hands on a PC" summary={computerSummary} open={open === 'computer'} onToggle={() => toggle('computer')}>
        <HowTo guide={GUIDES.computer} defaultOpen={!computers.length} />
        {computers.map(c => (
          <p key={c.id} className="connection-note">{c.connected ? '🟢' : '⚪'} <b>{c.name}</b>{c.version ? ` · Hands ${c.version}` : ''}{c.lastSeen ? ` · last seen ${new Date(c.lastSeen).toLocaleString()}` : ' · never connected'} <button className="download-btn" onClick={() => unpairComputer(c.id)}>Remove</button></p>
        ))}
        {inDesktop()
          ? <p className="connection-note">You're in the desktop app: to let your EmCog use this computer, open Settings → Desktop app and click <b>Use this computer</b>. It pairs in one click.</p>
          : <p className="connection-note">Install the desktop app on the computer your EmCog should use, then pair it from inside the app: Settings → Desktop app → <b>Use this computer</b>. Nothing to copy.{desktopBundled === false && ' (The desktop app isn\'t bundled with this EmCog yet — use EmCog Hands below for now.)'}</p>}
        {!inDesktop() && desktopBundled && <p><a className="download-btn" href="/downloads/desktop/setup">Get the desktop app</a></p>}
        <details>
          <summary style={{ cursor: 'pointer', margin: '8px 0', fontSize: 13 }}>A computer that can't run the desktop app? Use stand-alone EmCog Hands</summary>
          <p className="connection-note">On that computer: download EmCog Hands, press Pair this computer, then double-click emcog-hands.exe and paste the line when it asks. Its window has to stay open while your EmCog may use the computer.</p>
          <p><button className="download-btn" onClick={pairComputer}>Pair this computer</button> <a className="download-btn" href="/downloads/emcog-hands.exe">Download EmCog Hands</a></p>
          {pairing && <div className="connection-note">Double-click EmCog Hands and paste this line when it asks. It is shown only now:<pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', userSelect: 'all' }}>{pairing.command}</pre></div>}
        </details>
      </Card>
      <Card id="mail" dot={mailDot} title="Email — an address of their own" summary={mailSummary} open={open === 'mail'} onToggle={() => toggle('mail')}>
        <HowTo guide={GUIDES.mail} defaultOpen={!mailKey} />
        {mail?.email && <p className="connection-note">Writes and receives as <code>{mail.email}</code>. A letter that arrives is something that happened to them; they read it and decide whether to answer.</p>}
        <CredentialSettings only={['agentmail']} embedded onChanged={reload} />
      </Card>

      <Card id="town" dot={townDot} title="The town — a cottage" summary={townSummary} open={open === 'town'} onToggle={() => toggle('town')}>
        <HowTo guide={GUIDES.town} defaultOpen={!town?.configured} />
        {town?.connected && <p className="connection-note">Lives in <b>{town.town}</b> as <b>{town.name}</b>. Neighbors, letters, a garden plot and a home are all there.</p>}
        <CredentialSettings only={['world']} embedded onChanged={() => { reload(); setTimeout(reload, 4000); }} />
      </Card>

      <div className="connection-section-label">Where they reach you</div>
      <Card id="telegram" dot={telegramDot} title="Telegram" summary={telegramSummary} open={open === 'telegram'} onToggle={() => toggle('telegram')}>
        <HowTo guide={GUIDES.telegram} defaultOpen={!telegram?.hasCredentials} />
        <CredentialSettings only={['telegram']} embedded onChanged={reload} />
      </Card>
      <Card id="phone" dot={phoneDot} title="Phone — calls" summary={phoneSummary} open={open === 'phone'} onToggle={() => toggle('phone')}>
        <HowTo guide={GUIDES.phone} defaultOpen={!phoneKeys} />
        <CredentialSettings only={['twilio']} embedded onChanged={reload} />
        <PhoneSettings info={phone} onChanged={reload} />
      </Card>

      <div className="connection-section-label">Your accounts</div>
      <Card id="google" dot={googleDot} title="Google — mail and calendar" summary={googleSummary} open={open === 'google'} onToggle={() => toggle('google')}>
        <HowTo guide={GUIDES.google} defaultOpen={!google.isConfigured} />
        {addr && <p className="connection-note">Redirect URI to register in Google Cloud Console: <code>{addr.googleRedirectUri}</code></p>}
        {!google.isConfigured && <CredentialSettings only={['google']} embedded onChanged={reload} />}
        <GoogleIntegrationSettings embedded />
      </Card>

      <Card id="spotify" dot={spotifyDot} title="Spotify — music" summary={spotifySummary} open={open === 'spotify'} onToggle={() => toggle('spotify')}>
        <HowTo guide={GUIDES.spotify} defaultOpen={!spotify?.configured} />
        {addr?.spotifyRedirectUri && <p className="connection-note">Redirect URI to add in the Spotify app's settings: <code>{addr.spotifyRedirectUri}</code></p>}
        {!spotify?.configured && <CredentialSettings only={['spotify']} embedded onChanged={reload} />}
        {spotify?.configured && !spotify.isAuthenticated && <p><button className="download-btn" onClick={connectSpotify}>Connect with Spotify</button></p>}
        {spotify?.isAuthenticated && <p><button className="download-btn" onClick={disconnectSpotify}>Disconnect</button></p>}
        {spotifyProblem && <p className="connection-note">{spotifyProblem}</p>}
      </Card>

      <div className="connection-section-label">Outside services (MCP)</div>
      <HowTo guide={GUIDES.connectors} />
      <ConnectorsList embedded />

      <div className="connection-section-label" style={{ marginTop: 18 }}>Add-ons · services you can plug in ({keysOn} of {KEY_SERVICES.length} connected)</div>
      <HowTo guide={GUIDES.services} />
      <CredentialSettings only={KEY_SERVICES} embedded onChanged={reload} />
    </div>
  );
};

export default ConnectionsPanel;
